/**
 * Shopify orders & transactions list APIs (auth required).
 */

import { getApiBaseUrl, getBaseHeaders } from "./api";

function getHeaders({ accessToken } = {}) {
    const headers = { ...getBaseHeaders() };
    if (accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
    }
    return headers;
}

function buildQuery(params) {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v != null && v !== "") search.set(k, String(v));
    });
    const q = search.toString();
    return q ? `?${q}` : "";
}

/**
 * GET /api/orders
 * @param {{ page?: number, limit?: number, ref_provider?: string, source_ref?: string, accessToken?: string }} params
 * @returns {Promise<{ ok: boolean, data?: { orders: array, total: number, page: number, limit: number }, error?: string }>}
 */
export async function fetchOrders({ page, limit, ref_provider, source_ref, accessToken } = {}) {
    const base = getApiBaseUrl();
    const q = buildQuery({ page, limit, ref_provider, source_ref });
    const res = await fetch(`${base}/api/orders${q}`, {
        method: "GET",
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}

/**
 * GET /api/transactions
 * @param {{ page?: number, limit?: number, ref_provider?: string, source_ref?: string, accessToken?: string }} params
 * @returns {Promise<{ ok: boolean, data?: { transactions: array, total: number, page: number, limit: number }, error?: string }>}
 */
export async function fetchTransactions({ page, limit, ref_provider, source_ref, accessToken } = {}) {
    const base = getApiBaseUrl();
    const q = buildQuery({ page, limit, ref_provider, source_ref });
    const res = await fetch(`${base}/api/transactions${q}`, {
        method: "GET",
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}

/**
 * GET /api/overview
 * Financial overview: allTimeEarnings, lastWithdrawal, linkedAccount, monthlyEarnings[]. Auth required.
 * @param {{ accessToken?: string }} params
 * @returns {Promise<{ ok: boolean, data?: { allTimeEarnings: number, lastWithdrawal: number, linkedAccount: string, monthlyEarnings: Array<{day: number, earnings: number}>, currency: string }, error?: string }>}
 */
export async function fetchOverview({ accessToken } = {}) {
    const base = getApiBaseUrl();
    const res = await fetch(`${base}/api/overview`, {
        method: "GET",
        headers: getHeaders({ accessToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.success === true && json.data) {
        return { ok: true, data: json.data };
    }
    return {
        ok: false,
        error: json?.error?.message || `Request failed (${res.status})`,
    };
}
